import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  className?: string;
  /** Force color regardless of direction */
  color?: string;
  /** Show last-point dot */
  showDot?: boolean;
  /** Stroke width */
  strokeWidth?: number;
}

/**
 * Quiver-style inline sparkline. Auto-colored green/red by first→last delta,
 * unless `color` is provided. Pure SVG, zero deps. Renders nothing if <2 points.
 */
const Sparkline = ({
  data,
  width = 80,
  height = 24,
  className,
  color,
  showDot = false,
  strokeWidth = 1.25,
}: SparklineProps) => {
  const { path, stroke, lastX, lastY } = useMemo(() => {
    if (!data || data.length < 2) return { path: '', stroke: '', lastX: 0, lastY: 0 };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = width / (data.length - 1);
    const points = data.map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / range) * height;
      return [x, y] as const;
    });
    const d = points
      .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`)
      .join(' ');
    const direction = data[data.length - 1] - data[0];
    const autoStroke = color
      ? color
      : direction >= 0
      ? 'hsl(var(--positive))'
      : 'hsl(var(--negative))';
    const [lx, ly] = points[points.length - 1];
    return { path: d, stroke: autoStroke, lastX: lx, lastY: ly };
  }, [data, width, height, color]);

  if (!path) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60',
          className,
        )}
        style={{ width, height }}
      >
        N/A
      </span>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('inline-block align-middle', className)}
      aria-hidden
    >
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showDot && (
        <circle cx={lastX} cy={lastY} r={1.75} fill={stroke} />
      )}
    </svg>
  );
};

export default Sparkline;
